import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Dropdown } from '@/components/Dropdown';
import { createQueryExportConfig } from '@/components/export/configs/query.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { ExportFormat, type ExportFormatType } from '@/hooks/useReportExport';
import { ExportPDFReport } from '@/sections/export/ExportPDFReport';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { useAuth } from '@/state/auth';

interface QueryBuilderHeaderButtonsProps {
  returnedRiskKeys?: string[];
}

export function QueryBuilderHeaderButtons({
  returnedRiskKeys = [],
}: QueryBuilderHeaderButtonsProps) {
  const { isMaximized, toggleMaximized, mode, queryState, getSavedQuery } =
    useQueryBuilder();
  const { isPraetorianUser } = useAuth();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportFormat, setReportFormat] = useState<ExportFormatType>(
    ExportFormat.PDF
  );

  // Compute export config from current query
  const exportConfig = useMemo(() => {
    if (!queryState) return null;
    const savedQuery = getSavedQuery?.();
    if (!savedQuery) return null;
    return createQueryExportConfig(savedQuery);
  }, [queryState, getSavedQuery]);

  // Determine if export is available
  const canExport = mode === 'query' && !!exportConfig;
  const isRiskExport =
    exportConfig?.apiEntityType === 'risk' ||
    exportConfig?.apiEntityType === 'vulnerability';
  const showRiskExportMenu = canExport && isPraetorianUser && isRiskExport;
  const riskExportItemKeys = isRiskExport ? returnedRiskKeys : [];

  const exportTooltip =
    mode !== 'query'
      ? 'Run a query to enable export'
      : !exportConfig
        ? 'This entity type does not support export'
        : 'Export query results';

  return (
    <>
      <div className="flex flex-shrink-0 items-center gap-2">
        {showRiskExportMenu ? (
          <Dropdown
            asChild
            menu={{
              placement: 'bottom-end',
              items: [
                {
                  label: 'Export Data',
                  value: 'export-data',
                  onClick: () => setIsExportModalOpen(true),
                },
                {
                  label: 'Generate PDF Report',
                  value: 'generate-pdf',
                  onClick: () => {
                    setReportFormat(ExportFormat.PDF);
                    setReportModalOpen(true);
                  },
                },
                {
                  label: 'Generate ZIP Report',
                  value: 'generate-zip',
                  onClick: () => {
                    setReportFormat(ExportFormat.ZIP);
                    setReportModalOpen(true);
                  },
                },
              ],
            }}
          >
            <button
              className="text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Export query results"
              data-testid="query-builder-export-button"
              title={exportTooltip}
            >
              <Download className="h-5 w-5" />
            </button>
          </Dropdown>
        ) : (
          <button
            onClick={() => setIsExportModalOpen(true)}
            disabled={!canExport}
            className="text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Export query results"
            data-testid="query-builder-export-button"
            title={exportTooltip}
          >
            <Download className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={toggleMaximized}
          className="text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary rounded-md p-1 transition-colors"
          aria-label={
            isMaximized ? 'Exit maximized mode' : 'Maximize workspace'
          }
          data-testid="query-builder-maximize-toggle"
        >
          {isMaximized ? (
            <Minimize2 className="h-5 w-5" />
          ) : (
            <Maximize2 className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Screen reader announcements */}
      <div className="sr-only" role="status" aria-live="polite">
        {isMaximized
          ? 'Query Builder maximized'
          : 'Query Builder restored to normal view'}
      </div>

      {exportConfig && isExportModalOpen && (
        <UniversalExportModal
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          config={exportConfig}
          selectedRows={riskExportItemKeys}
        />
      )}

      {showRiskExportMenu && (
        <ExportPDFReport
          selectedRows={riskExportItemKeys}
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
          initialExportFormat={reportFormat}
          skipMarkdownDefaults
        />
      )}
    </>
  );
}
